import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Bus, TriangleAlert } from 'lucide-react';

import { landingPath } from '../lib/shared';
import type { Role } from '@parkline/shared';
import { useAuth, type RoleChoice } from '../lib/auth';
import { useDemoMode } from '../lib/demo-mode';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Field';

/**
 * Учётки стенда. Показываются в dev-сборке и на стенде с заглушками: заказчику на показе
 * нужно переключаться между кабинетами, не держа список в переписке. В боевом режиме
 * (`demoMode: false`) блок не отображается.
 *
 * Выдуманных учёток здесь больше нет — на стенде их тоже нет. Остались работники парка и
 * администратор: список должен совпадать с тем, что в системе, иначе подсказка на входе
 * отправляет туда, где никого.
 */
const DEV_ACCOUNTS: readonly { login: string; role: string; esutd?: boolean }[] = [
  { login: 'a.aitaurov', role: 'Диспетчер, Айтауров А.Е.' },
  { login: 'b.nurshaeva', role: 'Медработник, Нуршаева Б.С.' },
  { login: 'e.ibraimov', role: 'Водитель, Ибраимов Е.М.' },
  { login: 'admin', role: 'Администратор' },
  /**
   * Работники, связанные с настоящей ЕСУТД. Отделены от прочих намеренно: только их ИИН
   * заведены на той стороне, и только с ними путевой лист туда доходит. С остальными
   * интерфейс посмотреть можно, а смена в ЕСУТД не зарегистрируется.
   */
  {
    login: 'k.kusainova',
    role: 'медработник и механик, она же подписант — роль выбирается при входе',
    esutd: true,
  },
  { login: 'a.vilnovich', role: 'водитель Вильнович А.Ф., борт А2028', esutd: true },
  { login: 'k.sultanov', role: 'водитель Султанов К.Т., борт А798', esutd: true },
];
const DEV_PASSWORD = 'parkline123';

export function LoginPage() {
  const { user, login: signIn, selectRole, loading, can } = useAuth();
  const location = useLocation();
  const demo = useDemoMode();
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Второй шаг входа: пароль принят, но ролей несколько и надо выбрать одну.
  const [choice, setChoice] = useState<RoleChoice | null>(null);

  // Раздел, с которого пользователя увели на вход, либо стартовый раздел его роли.
  const from = (location.state as { from?: string } | null)?.from ?? landingPath(can);

  if (user && !loading) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      setChoice(await signIn(loginName.trim(), password));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось войти');
    } finally {
      setSubmitting(false);
    }
  };

  const pickRole = async (role: Role) => {
    if (!choice) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await selectRole(choice.preAuthToken, role);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось войти в выбранной роли');
      // Промежуточный токен живёт минуты: если он истёк, начинаем с пароля заново.
      setChoice(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600 text-white">
            <Bus className="h-7 w-7" aria-hidden />
          </span>
          <h1 className="text-2xl font-semibold text-slate-900">Parkline AI</h1>
          <p className="text-sm text-slate-500">
            Управление ежедневным выпуском автобусного парка
          </p>
        </div>

        {choice ? (
          <div className="card flex flex-col gap-4 p-5">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Кем вы сегодня работаете?</h2>
              <p className="mt-1 text-sm text-slate-500">
                У вас несколько ролей. Выберите одну — интерфейс покажет только её работу.
                Сменить роль можно потом, не выходя из системы.
              </p>
            </div>
            {error ? (
              <p
                className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
                role="alert"
              >
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{error}</span>
              </p>
            ) : null}
            {choice.roles.map((item) => (
              <Button
                key={item.role}
                type="button"
                disabled={submitting}
                onClick={() => void pickRole(item.role)}
              >
                {item.label}
              </Button>
            ))}
            <button
              type="button"
              className="text-sm text-slate-500 underline"
              onClick={() => setChoice(null)}
            >
              Войти другой учётной записью
            </button>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="card flex flex-col gap-4 p-5">
          <Field label="Логин" required>
            {(id) => (
              <Input
                id={id}
                name="login"
                autoComplete="username"
                autoFocus
                required
                value={loginName}
                onChange={(event) => setLoginName(event.target.value)}
                placeholder="Например, a.aitaurov"
              />
            )}
          </Field>

          <Field label="Пароль" required>
            {(id) => (
              <Input
                id={id}
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          {error ? (
            <p
              className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
              role="alert"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </p>
          ) : null}

          <Button type="submit" size="lg" block loading={submitting}>
            Войти
          </Button>
        </form>
        )}

        {import.meta.env.DEV || demo?.demoMode ? (
          <DevAccountsHint
            onPick={(value) => {
              setLoginName(value);
              setPassword(DEV_PASSWORD);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function DevAccountsHint({ onPick }: { onPick: (login: string) => void }) {
  return (
    <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-white/70 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Учётки стенда, пароль {DEV_PASSWORD}
      </p>
      <AccountList accounts={DEV_ACCOUNTS.filter((account) => !account.esutd)} onPick={onPick} />

      {/*
        Учётные записи, связанные с настоящей ЕСУТД, отделены намеренно: только их ИИН
        заведены на той стороне, и только с ними путевой лист туда доходит. С остальными
        интерфейс посмотреть можно, а смена в ЕСУТД не зарегистрируется — и без этой
        подсказки человек выбирает первую попавшуюся и упирается в отказ.
      */}
      <p className="mb-2 mt-4 border-t border-slate-200 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Пилот «Автобусный парк №1» — только с ними уходит путевой лист
      </p>
      <AccountList accounts={DEV_ACCOUNTS.filter((account) => account.esutd)} onPick={onPick} />
    </div>
  );
}

function AccountList({
  accounts,
  onPick,
}: {
  accounts: readonly { login: string; role: string }[];
  onPick: (login: string) => void;
}) {
  return (
    <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
      {accounts.map((account) => (
        <li key={account.login}>
          <button
            type="button"
            onClick={() => onPick(account.login)}
            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
          >
            <span className="font-mono">{account.login}</span>
            <span className="text-xs text-slate-500">{account.role}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export default LoginPage;
